import { useEffect, useState } from "react";

import AdvancedFilterSelect from "src/reusable/components/AdvancedFilterSelect";
import { Box, ToggleButton, Tooltip } from "@mui/material";
import { Star as StarIcon } from "@mui/icons-material";
import EqualFilterSelect from "src/reusable/components/EqualFilterSelect";
import { capitalizeFirstLetter } from "src/helpers/helperFunctions";
import { useSearchUsers } from "src/api/projectFolders/projectFolderService";
import { useGetFilteredDatasetTypes } from "../services/DatasetServices";
import { useGetLicensedProducts } from "src/views/managementConsole/services/LicenseService";
import { whereFilterType } from "src/types/types";

type DatasetFiltersProps = {
  whereFilters: whereFilterType[];
  setWhereFilters: React.Dispatch<React.SetStateAction<whereFilterType[]>>;
  atomResults?: boolean;
  showFavoritesOnly?: boolean;
  onToggleFavorites?: (value: boolean) => void;
};

const DatasetFilters = ({
  whereFilters,
  setWhereFilters,
  atomResults = false,
  showFavoritesOnly = false,
  onToggleFavorites,
}: DatasetFiltersProps) => {
  const [datasetTypes, setDatasetTypes] = useState<
    { value: string; label: string }[]
  >([]);
  const [users, setUsers] = useState<{ value: string; label: string }[]>([]);
  const [userQuery, setUserQuery] = useState("");

  const { data: licensedProducts, isError: isLicenseError } =
    useGetLicensedProducts();
  const { data: datasetTypesData } = useGetFilteredDatasetTypes(
    licensedProducts,
    isLicenseError,
  );
  // /user/search is readable by every user (unlike admin-only /user/all) and
  // returns matches on username or display name as the user types.
  const { data: usersData } = useSearchUsers(userQuery);

  useEffect(() => {
    if (datasetTypesData) {
      setDatasetTypes(
        datasetTypesData
          ?.filter((d: any) => d.name !== "AtomResults")
          .map((datasetType: any) => ({
            value: datasetType.name,
            label: capitalizeFirstLetter(String(datasetType.name)),
          })) || [],
      );
    }
  }, [datasetTypesData]);

  useEffect(() => {
    if (usersData) {
      setUsers(
        usersData
          // The backend resolves the create_user.username filter against the
          // display name column, so the display name is the filter value.
          .filter((user) => user.user_display_name != null)
          .map((user) => ({
            value: user.user_display_name as string,
            label: user.user_display_name as string,
          })),
      );
    }
  }, [usersData]);

  return (
    <Box
      id="dataset-filters"
      data-testid="ds-filter-container"
      display="flex"
      gap={2}
      flexWrap="wrap"
      alignItems="center"
    >
      {!atomResults && onToggleFavorites && (
        <Tooltip
          title={
            showFavoritesOnly ? "Show all datasets" : "Show favorites only"
          }
        >
          <ToggleButton
            value="favorites"
            selected={showFavoritesOnly}
            onChange={() => onToggleFavorites(!showFavoritesOnly)}
            data-testid="ds-btn-favorites"
            data-attr="datasets:toggle:show-favorites-only"
            size="small"
            sx={{
              borderRadius: 1,
              px: 1.5,
              fontWeight: 500,
              backgroundColor: "background.paper",
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "background.paper",
                borderColor: "warning.main",
                color: "warning.main",
              },
              "&.Mui-selected": {
                backgroundColor: "warning.main",
                color: "common.white",
                borderColor: "warning.main",
                "&:hover": {
                  backgroundColor: "warning.dark",
                  borderColor: "warning.dark",
                },
              },
            }}
          >
            <StarIcon sx={{ mr: 0.5, fontSize: "1.1rem" }} />
            Favorites
          </ToggleButton>
        </Tooltip>
      )}
      {!atomResults && (
        <EqualFilterSelect
          label="Type"
          type="datasetType"
          column="dataset_type.name"
          data-testid="ds-select-type"
          whereFilters={whereFilters}
          setWhereFilters={setWhereFilters}
          options={datasetTypes}
        />
      )}
      {!atomResults && (
        <EqualFilterSelect
          label="Create user"
          column="create_user.username"
          data-testid="ds-select-create-user"
          whereFilters={whereFilters}
          setWhereFilters={setWhereFilters}
          options={users}
          onInputChange={setUserQuery}
        />
      )}
      <AdvancedFilterSelect
        label="Create date"
        columnName="create_date"
        data-testid="ds-btn-sort-date"
        valueType="date"
        whereFilters={whereFilters}
        setWhereFilters={setWhereFilters}
      />
      {!atomResults && (
        <EqualFilterSelect
          label="Visibility"
          column="visibility_level"
          data-testid="ds-select-visibility"
          whereFilters={whereFilters}
          setWhereFilters={setWhereFilters}
          options={[
            { value: "public", label: "Public" },
            { value: "private", label: "Private" },
          ]}
        />
      )}
    </Box>
  );
};

export default DatasetFilters;
